import styled from 'styled-components';

export const Container = styled.div`
  min-height: 100vh;
  background-color: var(--surface-canvas);
  padding: 2rem 0;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

export const Logo = styled.img`
  height: auto;
  max-width: 180px;
`;

export const Title = styled.h1`
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 1.5rem;
  color: var(--text-primary);
  text-align: center;
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

export const ErrorMessage = styled.div`
  background-color: var(--error);
  color: var(--text-on-inverse);
  padding: 1rem;
  border-radius: 0.5rem;
  margin: 1rem auto;
  max-width: 90%;
  text-align: center;
  font-family: var(--font-body);
  font-weight: 500;
`;

export const FormContainer = styled.form`
  background-color: var(--surface-raised);
  border-radius: 0.5rem;
  box-shadow: var(--elevation-3);
  margin: 0 auto;
  max-width: 1200px;
  width: 90%;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    width: 95%;
  }
`;

export const Section = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionTitle = styled.h3`
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 1.2rem;
  color: var(--text-primary);
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--action-primary-surface);
`;

export const InputGroup = styled.div`
  margin-bottom: 1.5rem;
  width: 100%;
`;

export const Label = styled.label`
  display: block;
  font-family: var(--font-body);
  font-weight: 400;
  font-size: 1rem;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  padding-left: 0.5rem;
`;

export const Required = styled.span`
  color: var(--error);
  font-weight: 700;
  font-size: 1.2rem;
  margin-right: 0.25rem;
`;

export const Note = styled.p`
  font-family: var(--font-body);
  font-weight: 400;
  font-size: 0.875rem;
  color: var(--error);
  margin-top: 0.25rem;
  padding-left: 0.5rem;
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid transparent;
  border-radius: 0.25rem;
  background-color: var(--surface-raised);
  box-shadow: var(--elevation-1);
  font-family: var(--font-body);
  font-size: 1rem;
  color: var(--text-primary);
  transition: all 0.3s ease;
  
  &::placeholder {
    color: var(--text-disabled);
    font-weight: 300;
  }
  
  &:hover,
  &:focus {
    border-color: var(--action-primary-surface);
    background-color: var(--success-surface);
    outline: none;
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid transparent;
  border-radius: 0.25rem;
  background-color: var(--surface-raised);
  box-shadow: var(--elevation-1);
  font-family: var(--font-body);
  font-size: 1rem;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover,
  &:focus {
    border-color: var(--action-primary-surface);
    background-color: var(--success-surface);
    outline: none;
  }
  
  option {
    background-color: var(--surface-raised);
    color: var(--text-primary);
    font-weight: 400;
    padding: 0.5rem;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: var(--surface-raised);
  border-radius: 0.25rem;
  overflow: hidden;
  box-shadow: var(--elevation-1);
`;

export const TableHeader = styled.thead`
  background-color: var(--action-primary-surface);
`;

export const TableBody = styled.tbody`
  background-color: var(--surface-raised);
`;

export const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: var(--surface-canvas);
  }
  
  &:hover {
    background-color: var(--success-surface);
  }
`;

export const TableHeaderCell = styled.th<{ width?: string }>`
  padding: 1rem;
  text-align: left;
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 0.9rem;
  color: var(--surface-raised);
  width: ${props => props.width || 'auto'};
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    font-size: 0.8rem;
  }
`;

export const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid var(--border-default);
  font-family: var(--font-body);
  font-size: 0.9rem;
  color: var(--text-primary);
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    font-size: 0.8rem;
  }
`;

export const TableInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--border-default);
  border-radius: 0.25rem;
  background-color: var(--surface-raised);
  font-family: var(--font-body);
  font-size: 0.9rem;
  color: var(--text-primary);
  transition: all 0.3s ease;
  
  &::placeholder {
    color: var(--text-disabled);
  }
  
  &:hover,
  &:focus {
    border-color: var(--action-primary-surface);
    outline: none;
  }
  
  &:read-only {
    background-color: var(--surface-sunken);
    cursor: not-allowed;
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid var(--border-default);
`;

export const SubmitButton = styled.button`
  background-color: var(--action-primary-surface);
  color: var(--surface-raised);
  border: none;
  border-radius: 0.5rem;
  padding: 1rem 2rem;
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 200px;
  
  &:hover {
    background-color: var(--text-secondary);
    transform: translateY(-2px);
    box-shadow: var(--elevation-3);
  }
  
  &:disabled {
    background-color: var(--text-disabled);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: auto;
  }
`;